import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  viewChild,
  OnDestroy,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DepartmentService } from '../department.service';
import { DepartmentDetails } from '../department-model';
import { DualLangInputComponent } from '@areeb-innovative-technologies/angular_multi_languages_input_lib';
import { Subscription } from 'rxjs';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { SelectComponent } from '../../../../../../ui-components/lib/select/select.component';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import { InputComponent } from '../../../../../../ui-components/lib/input/input.component';
import { UploadFilesComponent } from '../../../../../../ui-components/lib/upload-files/upload-files.component';
import { PaginatedSelectComponent } from '../../../../../../ui-components/lib/paginated-select/paginated-select.component';
import { ScrollToErrorDirective } from '../../../../../common/directives/scroll-to-error/scroll-to-error.directive';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { FormService } from '../../../../../../form-builder/services/form/form.service';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { FormValidation } from '../../../../../../ui-components/enums/form-validation';
import { dualLanguageCustomValidators } from '../../../../../../form-builder/services/form/custom-validators';
import { OptionItem } from '../../../../../../ui-components/lib/option.interface';
import {
  LookupFilter,
  LookupOptionItem,
} from '../../../../../../ui-components/lib/lookup-field/lookup-field-item';
import {
  baseUrl,
  ENDPOINTS,
} from '../../../../../common/constants/endpoints.constants';
import { filterInputTypesEnum } from '../../../../../../ui-components/enums/filter-input-type';
import { uploadFileUrls } from '../../../../../common/constants/upload-file-url';
import { Attachment } from '../../../../../../ui-components/lib/upload-files/attachment.interface';
import { Status } from '../../../../../common/enums/user-status';

@Component({
  selector: 'lib-department-form',
  imports: [
    OverlayLoaderComponent,
    ReactiveFormsModule,
    SelectComponent,
    ValidationMessageComponent,
    InputComponent,
    TranslocoPipe,
    UploadFilesComponent,
    PaginatedSelectComponent,
    DualLangInputComponent,
    RouterLink,
    ScrollToErrorDirective,
    TenantPathPipe,
  ],
  templateUrl: './department-form.component.html',
  styleUrl: './department-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DepartmentFormComponent implements OnDestroy {
  readonly ADMIN_ROUTES = ADMIN_ROUTES;

  departmentService = inject(DepartmentService);
  route = inject(ActivatedRoute);
  formService = inject(FormService);
  router = inject(Router);
  transloco = inject(TranslocoService);
  scrollToError = viewChild.required(ScrollToErrorDirective);
  toasterService = inject(ToasterService);
  tenantService = inject(TenantService);
  subscriptions = new Subscription();

  departmentFormGroup = signal<FormGroup>(
    new FormGroup({
      image: new FormControl(''),
      name: new FormGroup(
        {
          en: new FormControl('', {
            validators: [Validators.maxLength(FormValidation.maxLength)],
          }),
          ar: new FormControl('', {
            validators: [Validators.maxLength(FormValidation.maxLength)],
          }),
        },
        {
          validators: [dualLanguageCustomValidators.atLeastOneLangRequired],
        }
      ),
      identifier: new FormControl('', [
        Validators.maxLength(FormValidation.identifierMaxLength),
      ]),
      legal_entity_id: new FormControl('', Validators.required),
      parent_id: new FormControl(''),
      direct_manager_id: new FormControl(''),
      department_type_option_id: new FormControl(null),
    })
  );
  isLoading = signal<boolean>(true);
  pageTitle = signal<string>('');
  departmentDetails!: DepartmentDetails;
  editMode = signal<boolean>(false);
  buttonText = signal<string>('');

  isFormChanged = signal<boolean>(false);
  legalEntityId = signal<number>(0);
  legalEntity = signal<OptionItem[]>([]);

  selectedDepartmentType!: LookupOptionItem;

  readonly variableOptionsApi = ENDPOINTS.Lookup.VariableOptionsSorted;
  readonly departmentTypeFilter: LookupFilter[] = [
    {
      key: 'variable.module',
      value: 'user_management',
      value_type: filterInputTypesEnum.STATIC,
    },
    {
      key: 'variable.key',
      value: 'department_type',
      value_type: filterInputTypesEnum.STATIC,
    },
    {
      key: 'is_active',
      value: '1',
      value_type: filterInputTypesEnum.STATIC,
    },
  ];

  parentApi = `${baseUrl}/lookup/departments`;
  parentFilter: LookupFilter[] = [
    {
      key: 'legal_entity_id',
      value: 'legal_entity_id',
      value_type: filterInputTypesEnum.DYNAMIC,
      multiple: false,
      required: true,
    },
  ];
  selectedParent!: LookupOptionItem;

  directManagerApi = `${ENDPOINTS.Lookup.User}?filters[not_states][0]=${Status.INACTIVE}&filters[not_states][1]=${Status.CANCELLED}&filters[not_states][2]=${Status.DELETED}`;
  selectedDirectManager!: LookupOptionItem;

  initialFormValues: any = {};

  uploadFileUrls = uploadFileUrls;
  uploadedImage = signal<Attachment[]>([]);

  constructor() {
    this.getLegalEntities();
    this.getDepartmentId();
  }

  getLegalEntities() {
    this.subscriptions.add(
      this.departmentService
        .getLegalEntities()
        .subscribe((response: { data: any }) => {
          this.legalEntity.set(response.data);
        })
    );
  }

  getDepartmentId() {
    this.subscriptions.add(
      this.route.params.subscribe(params => {
        if (params['id']) {
          this.pageTitle.set(
            this.transloco.translate('department_form_screen_edit_title')
          );
          this.buttonText.set(
            this.transloco.translate('department_form_screen_save_edits_button')
          );
          this.parentFilter = [
            {
              key: 'legal_entity_id',
              value: 'legal_entity_id',
              value_type: filterInputTypesEnum.DYNAMIC,
              multiple: false,
              required: true,
            },
            {
              key: 'not_id',
              value: params['id'],
              value_type: filterInputTypesEnum.STATIC,
              multiple: false,
              required: true,
            },
            {
              key: 'not_child_for_id',
              value: params['id'],
              value_type: filterInputTypesEnum.STATIC,
              multiple: false,
              required: true,
            },
          ];
          this.subscriptions.add(
            this.departmentService.getDepartment(params['id']).subscribe({
              next: (response: { data: DepartmentDetails }) => {
                this.departmentDetails = response.data;
                this.editMode.set(true);
                this.patchForm(response.data);
              },
              error: () => {
                this.isLoading.set(false);
                this.router.navigate([
                  this.tenantService.adminPath(
                    `${ADMIN_ROUTES.DEPARTMENTS.LIST}`
                  ),
                ]);
              },
            })
          );
        } else {
          this.pageTitle.set(
            this.transloco.translate('department_form_screen_add_title')
          );
          this.buttonText.set(
            this.transloco.translate(
              'department_form_screen_add_department_button'
            )
          );
          this.isLoading.set(false);
        }
      })
    );
  }

  patchForm(data: DepartmentDetails) {
    setTimeout(() => {
      this.departmentFormGroup()
        .get('name.en')
        ?.setValue(data.translations.name.en);
      this.departmentFormGroup()
        .get('name.ar')
        ?.setValue(data.translations.name.ar);
      this.departmentFormGroup().get('identifier')?.setValue(data.identifier);
      this.departmentFormGroup()
        .get('legal_entity_id')
        ?.setValue(data.legal_entity.id);

      this.departmentFormGroup().get('parent_id')?.setValue(data.parent?.id);
      if (data.parent) {
        this.selectedParent = {
          value: Number(data.parent.id),
          label: data.parent.name as string,
        };
      }

      this.departmentFormGroup()
        .get('direct_manager_id')
        ?.setValue(data.direct_manager?.id);
      if (data.direct_manager) {
        this.selectedDirectManager = {
          value: Number(data.direct_manager.id),
          label: data.direct_manager.name as string,
        };
      }

      if (data.image?.url) {
        this.departmentFormGroup()
          .get('image')
          ?.setValue(data.image ? [data.image.id] : null);

        this.uploadedImage.set([data.image as Attachment]);
      }

      this.departmentFormGroup()
        .get('department_type_option_id')
        ?.setValue(data.department_type_option_id ?? null);
      if (data.department_type) {
        this.selectedDepartmentType = {
          value: data.department_type_option_id as number,
          label: data.department_type.label,
        };
      }

      this.isLoading.set(false);
    }, 10);
  }

  goBack() {
    history.back();
  }

  submit() {
    this.formService.syncDualLangControls(this.departmentFormGroup());

    if (this.departmentFormGroup().invalid) {
      this.departmentFormGroup().markAllAsTouched();
      this.scrollToError().scrollToFirstError();
    } else {
      this.isLoading.set(true);

      const departmentForm = this.prepareFormData();

      if (this.editMode()) {
        if (!departmentForm.parent_id) {
          departmentForm.parent_id = '';
        }
        this.updateDepartment(departmentForm);
      } else {
        this.addDepartment(departmentForm);
      }
    }
  }

  prepareFormData() {
    const formValue = this.departmentFormGroup().getRawValue();
    const data: any = {
      name: {
        en: formValue.name.en,
        ar: formValue.name.ar,
      },
      identifier: formValue.identifier ?? '',
      legal_entity_id: formValue.legal_entity_id,
      parent_id: formValue.parent_id ?? '',
      direct_manager_id: formValue.direct_manager_id ?? '',
      image: formValue.image ?? '',
    };

    data.department_type_option_id =
      formValue.department_type_option_id ?? null;

    return data;
  }

  updateDepartment(departmentForm: any) {
    this.subscriptions.add(
      this.departmentService
        .updateDepartment(departmentForm, this.departmentDetails.id)
        .subscribe({
          next: () => {
            this.router.navigate([
              this.tenantService.adminPath(
                `${ADMIN_ROUTES.DEPARTMENTS.DETAILS}`
              ),
              this.departmentDetails.id,
            ]);
            this.isLoading.set(false);
          },
          error: error => this.handleError(error),
        })
    );
  }

  addDepartment(departmentForm: any) {
    this.subscriptions.add(
      this.departmentService.addDepartment(departmentForm).subscribe({
        next: () => {
          this.router.navigate([
            this.tenantService.adminPath(`${ADMIN_ROUTES.DEPARTMENTS.LIST}`),
          ]);
          this.isLoading.set(false);
        },
        error: error => this.handleError(error),
      })
    );
  }

  handleError(error: any) {
    this.isLoading.set(false);
    this.formService.addParentFormGroupErrors(
      error,
      this.departmentFormGroup()
    );
    this.scrollToError().scrollToFirstError(false);
    this.toasterService.add(
      error.message ? error.message : error.error?.message,
      '',
      '',
      'error',
      true,
      true
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
